import { useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';

import type { Locale } from 'common/enums/Locale';
import { executeQuery } from 'common/gql';
import { toGQLLocale } from 'common/gql/utils/gql-locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import howToTakeAGoodPictureQuery from '../../gql/query/howToTakeGoodPicture.gql';
import type {
  HowToTakeAGoodPictureContent,
  HowToTakeAGoodPictureQueryResponse,
  HowToTakeAGoodPictureQueryVariables,
} from '../../gql/query/types';
import { useErrorMessage } from './';

interface UsePictureGuideButtonClickHandlerOptions {
  setPictureGuideOpen: () => void;
  setIsFooterVisible: (value: boolean) => void;
}

export const usePictureGuideButtonClickHandler =
  ({ setPictureGuideOpen, setIsFooterVisible }: UsePictureGuideButtonClickHandlerOptions) =>
  () => {
    setPictureGuideOpen();
    setIsFooterVisible(false);
  };

const fetchPictureGuideContent = async (locale: Locale) => {
  const {
    content: { howToTakeAGoodPicture },
  } = await executeQuery<HowToTakeAGoodPictureQueryResponse, HowToTakeAGoodPictureQueryVariables>({
    query: howToTakeAGoodPictureQuery,
    variables: { locale: toGQLLocale(locale) },
    useApq: true,
  });

  return howToTakeAGoodPicture;
};

let pictureGuideContentCache: Nullable<HowToTakeAGoodPictureContent> = null;

export const usePictureGuideContent = () => {
  const [content, setContent] = useState(pictureGuideContentCache);
  const { locale } = useLocalization();
  const [errorMessage, setErrorMessage] = useErrorMessage();

  useEffect(() => {
    let isAlive = true;

    if (!content) {
      (async () => {
        try {
          const newContent = await fetchPictureGuideContent(locale);
          pictureGuideContentCache = newContent;

          if (isAlive) {
            setContent(newContent);
          }
        } catch {
          setErrorMessage();
        }
      })();
    }

    return () => {
      isAlive = false;
    };
  }, [locale, content]);

  return [content, errorMessage] as const;
};
